// =========================
// ForecastChart
// Plots `forecast` from the analyze-property response:
//   baseline:    { nightlyRate, occupancy }
//   projections: [{ month, nightlyRate, occupancy, confidence }]
// Two y-axes: USD on the left (rate), % on the right (occupancy + confidence).
// =========================

import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
} from "recharts";

const COLORS = {
  rate: "#2563eb",       // blue
  occupancy: "#16a34a",  // green
  confidenceFill: "#fde68a",
  confidenceLine: "#f59e0b",
  text: "#111827",
  subtle: "#6b7280",
};

function directionLabel(direction) {
  if (!direction) return "—";
  if (direction === "upward" || direction === "bullish") return "Bullish";
  if (direction === "downward" || direction === "bearish") return "Bearish";
  return "Flat";
}
function directionColor(direction) {
  if (direction === "upward" || direction === "bullish") return "#16a34a";
  if (direction === "downward" || direction === "bearish") return "#dc2626";
  return "#6b7280";
}

function fmtUsd(v) {
  if (typeof v !== "number" || !isFinite(v)) return "—";
  return `$${Math.round(v).toLocaleString()}`;
}
function fmtPct(v) {
  if (typeof v !== "number" || !isFinite(v)) return "—";
  return `${Math.round(v * 100)}%`;
}

export default function ForecastChart({ forecast }) {
  if (!forecast || !Array.isArray(forecast.projections) || forecast.projections.length === 0) {
    return (
      <div style={{ ...cardStyle, color: "#9ca3af", fontSize: 14 }}>
        No forecast data available yet — try again after more market history accumulates.
      </div>
    );
  }

  const baseline = forecast.baseline || {};
  const data = [
    {
      month: 0,
      nightlyRate: baseline.nightlyRate ?? null,
      occupancy: baseline.occupancy ?? null,
      confidence: forecast.confidenceScore ?? null,
    },
    ...forecast.projections.map((p) => ({
      month: p.month,
      nightlyRate: p.nightlyRate,
      occupancy: p.occupancy,
      confidence: p.confidence,
    })),
  ];

  const dir = directionLabel(forecast.direction);
  const dirColor = directionColor(forecast.direction);

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: COLORS.subtle, textTransform: "uppercase", letterSpacing: 1 }}>
          Forecast — {forecast.projections.length}-month horizon
        </div>
        <div style={{ fontSize: 13 }}>
          <span style={{ color: COLORS.subtle, marginRight: 6 }}>Direction:</span>
          <span style={{ color: dirColor, fontWeight: 700 }}>{dir}</span>
        </div>
      </div>

      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid stroke="#f3f4f6" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: COLORS.subtle }}
              label={{ value: "Months forward", position: "insideBottom", offset: -2, fill: COLORS.subtle, fontSize: 11 }}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12, fill: COLORS.subtle }}
              tickFormatter={(v) => `$${Math.round(v)}`}
              width={60}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 1]}
              tick={{ fontSize: 12, fill: COLORS.subtle }}
              tickFormatter={(v) => `${Math.round(v * 100)}%`}
              width={48}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === "Nightly rate") return [fmtUsd(value), name];
                return [fmtPct(value), name];
              }}
              labelFormatter={(label) => (label === 0 ? "Today (baseline)" : `Month +${label}`)}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine x={0} stroke="#9ca3af" strokeDasharray="3 3" yAxisId="left" />

            <Area
              yAxisId="right"
              type="monotone"
              dataKey="confidence"
              name="Confidence"
              stroke={COLORS.confidenceLine}
              fill={COLORS.confidenceFill}
              fillOpacity={0.4}
              isAnimationActive={false}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="nightlyRate"
              name="Nightly rate"
              stroke={COLORS.rate}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              isAnimationActive={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="occupancy"
              name="Occupancy"
              stroke={COLORS.occupancy}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div style={{ marginTop: 8, fontSize: 12, color: COLORS.subtle }}>
        Confidence decays with horizon distance and rises with lower volatility.
        Baseline (month 0) reflects today's adjusted market.
      </div>
    </div>
  );
}

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 20,
};
